import { useSEO } from "../components/SEO";
import { JsonLd } from "../components/JsonLd";
import { faqSchema } from "../lib/schema";
import { PageHero, Reveal } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const FAQS = [
  {
    q: "Who do you work with?",
    a: "Hajj & Umrah travel agencies and tour operators in India, from established operators to regional agencies building a professional acquisition system.",
  },
  {
    q: "Do you only work with Indian agencies?",
    a: "India is our primary market and where our specialization is deepest. If your business serves Indian pilgrims from elsewhere, get in touch and we'll tell you honestly whether we're a fit.",
  },
  {
    q: "Do you guarantee leads?",
    a: "No. Marketing performance varies based on market, offer, budget, seasonality, competition and your agency's sales process. We build the system and optimize it continuously. We don't sell guarantees.",
  },
  {
    q: "Do you guarantee bookings?",
    a: "No. Bookings depend on your packages, pricing and sales conversations. Our job is to generate qualified enquiries and strengthen the journey around them.",
  },
  {
    q: "Is ad spend included?",
    a: "No, unless explicitly stated in your agreement. Advertising spend is paid directly to the platforms and is separate from Nexus Catalyst's service fees.",
  },
  {
    q: "Do you manage Facebook and Instagram ads?",
    a: "Yes. Meta Ads management is part of the relevant service packages, including audience strategy, creative direction, lead generation and retargeting.",
  },
  {
    q: "Do you manage Google Ads?",
    a: "Yes, including search campaigns targeting high-intent Hajj and Umrah queries across India, with geographic targeting kept flexible around the markets you serve.",
  },
  {
    q: "Do you help with WhatsApp marketing?",
    a: "Yes, where included in your selected scope: enquiry flows, follow-up structure, customer segmentation, response frameworks and re-engagement.",
  },
  {
    q: "Do you create content?",
    a: "It depends on the selected service scope. Creative direction and campaign creative are part of most engagements; ongoing content calendars can be included where the scope calls for it. We'll define this clearly before any work begins.",
  },
  {
    q: "How quickly can campaigns launch?",
    a: "Timelines depend on scope, assets and approvals. Once the strategy phase is complete, a focused campaign can be prepared efficiently. We'll confirm a realistic timeline for your situation during the strategy call.",
  },
  {
    q: "What happens after a lead comes in?",
    a: "The intended journey: enquiry → WhatsApp conversation → qualification → consultation with your team → booking opportunity. We help structure that flow so fewer enquiries get lost between the ad and the sales conversation.",
  },
  {
    q: "Do you work with travel agencies outside Hajj & Umrah?",
    a: "Hajj & Umrah travel is our core specialization and where we do our best work. That focus is deliberate. It's what makes the marketing sharper.",
  },
  {
    q: "How can a Hajj agency get more leads?",
    a: "Combine Google Ads for high-intent package searches with Meta campaigns for demand generation, make WhatsApp your primary response channel, and plan campaigns around the season. Our resource hub covers the approach in detail.",
  },
  {
    q: "How much does Hajj marketing cost in India?",
    a: "Two components: a monthly service fee and separate advertising spend paid to the platforms. Our plans start at ₹20,000 per month, and ad budgets are planned per agency. See the pricing page for starting points.",
  },
  {
    q: "How do Hajj agencies convert enquiries into bookings?",
    a: "Fast WhatsApp responses, short structured qualification, one clear next step per conversation and scheduled follow-up. Conversion is an operations discipline as much as a marketing outcome.",
  },
  {
    q: "What is the best marketing strategy for a Hajj agency?",
    a: "Full-funnel and seasonal: capture existing search demand, generate new demand on Meta, build trust with credible content, convert through fast follow-up, and plan budgets around the pilgrimage calendar.",
  },
  {
    q: "How can WhatsApp help an Umrah travel agency?",
    a: "It turns slow form-based enquiries into immediate conversations: qualification in minutes, follow-up where customers actually respond, and re-engagement for prospects who are not ready yet.",
  },
];

export default function FAQ() {
  useSEO(
    "FAQ | Nexus Catalyst Agency",
    "Answers for Hajj & Umrah travel agencies in India: scope, pricing, ad spend, WhatsApp marketing, timelines and what happens after a lead comes in."
  );

  return (
    <main data-testid="faq-page">
      <JsonLd data={faqSchema(FAQS)} />
      <PageHero
        tag="FAQ"
        testid="faq-hero"
        crumbs={[{ label: "FAQ", to: "/faq" }]}
        lines={["Questions,", <span className="text-tan">answered.</span>]}
        copy="Straight answers for Hajj & Umrah travel agencies considering specialized growth marketing. If your question isn't here, ask us directly."
      />

      <section className="bg-white py-20 md:py-28" data-testid="faq-list">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-charcoal/10"
                  data-testid={`faq-item-${i + 1}`}
                >
                  <AccordionTrigger
                    className="text-left text-base md:text-lg font-semibold tracking-tight text-charcoal hover:text-tan hover:no-underline py-6"
                    data-testid={`faq-question-${i + 1}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-sm md:text-base text-charcoal/65 leading-relaxed pb-6 max-w-2xl"
                    data-testid={`faq-answer-${i + 1}`}
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Still Have a Question?"
        copy="Book a strategy call or download the Free Customer Growth Blueprint. We'll answer directly, specific to your agency."
        testid="faq-cta-band"
      />
    </main>
  );
}
